import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { noop } from 'ui-components/default-props/noop';
import style from './batch-components.module.less';
import { testId } from 'ui-components/services/testid-service';
import { emptyObject } from 'ui-components/default-props/empty';

const ErrorIcon = <FontAwesomeIcon className={style['error-icon']} icon={faExclamationCircle} />;

const EditBatchName = ({
  hasNameConflict = false,
  saveBatchNameChangeRequest = noop,
  batch = emptyObject,
  setInputValue = noop,
  handleShowEditInput = noop,
  inputValue = '',
}) => {
  const { name } = batch;
  const nameValue = name === inputValue ? name : inputValue;
  const [displayValue, setDisplayValue] = useState(nameValue);
  const input = useRef();
  const node = useRef();
  const inputHasError = hasNameConflict ? 'edit-name-error' : 'edit-name';
  const singleInputError = hasNameConflict ? 'single-input-with-error' : 'single-input';

  const onSaveNewBatchName = useCallback(() => {
    const updatedBatch = displayValue.length ? { ...batch, name: displayValue } : batch;
    setInputValue(displayValue);
    saveBatchNameChangeRequest(updatedBatch);
  }, [setInputValue, saveBatchNameChangeRequest, batch, displayValue]);

  const onChange = useCallback(
    (e) => {
      setDisplayValue(e.target.value);
    },
    [setDisplayValue],
  );

  const resetBatchNameAndInputValue = useCallback(() => {
    setInputValue(batch.name);
    saveBatchNameChangeRequest(batch);
  }, [setInputValue, saveBatchNameChangeRequest, batch]);

  const handleClickOutsideInputField = useCallback(() => {
    const hasNotChanged = inputValue === displayValue;
    if (hasNameConflict && hasNotChanged) {
      resetBatchNameAndInputValue();
      return;
    }
    onSaveNewBatchName();
    handleShowEditInput();
  }, [displayValue, handleShowEditInput, hasNameConflict, inputValue, onSaveNewBatchName, resetBatchNameAndInputValue]);

  const checkForEnterPress = useCallback(
    (e) => {
      if (e.key !== 'Enter') {
        return;
      }
      onSaveNewBatchName();
      handleShowEditInput();
    },
    [onSaveNewBatchName, handleShowEditInput],
  );

  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  return (
    <div ref={node} className={style[singleInputError]} {...testId('batch-name-editor')}>
      <input
        ref={input}
        className={style[inputHasError]}
        maxLength={50}
        onBlur={handleClickOutsideInputField}
        onChange={onChange}
        onKeyDown={checkForEnterPress}
        value={displayValue}
      />
      {hasNameConflict && (
        <div className={style['batch-already-exists']}>
          {ErrorIcon}
          <FormattedMessage defaultMessage='Batch names must be unique' description='nameExists' id='9WDZH6' />
        </div>
      )}
    </div>
  );
};

EditBatchName.propTypes = {
  hasNameConflict: PropTypes.bool,
  handleShowEditInput: PropTypes.func,
  saveBatchNameChangeRequest: PropTypes.func,
  batch: PropTypes.object,
  inputValue: PropTypes.string,
  setInputValue: PropTypes.func,
};

export default EditBatchName;
